import {Injectable} from '@angular/core';
import {BehaviorSubject, from, Observable, of} from 'rxjs'
import {DriverHttpService} from './drivers-http.service'
import {map, switchMap, tap} from "rxjs/operators";
import {FormBuilder} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {
    DriversFilter,
    IDriver,
    IDriverAccount,
    IDriverAttachmentInfo,
    IDriverAttachmentSaveData,
    IDriverDetailed,
    IDriverSaveData,
    IDriverTree
} from "@atl/lacerta-ui-common";

@UntilDestroy()
@Injectable()
export class DriversService {
    public activeDriverAccountsSubject: BehaviorSubject<IDriverAccount[] | null> = new BehaviorSubject<IDriverAccount[] | null>(null)
    public activeDriverAccounts$: Observable<IDriverAccount[] | null> = this.activeDriverAccountsSubject.asObservable()
    public filterForm = this.fb.group({
        modalFilter: this.fb.control({
            filter_error: null,
            filter_node: null,
            filter_status: null,
        }),
        filter_str: this.fb.control<string>('', {nonNullable: true}),
        filter_type: this.fb.control<number>(null),
    })
    public filterChange$ = this.filterForm.valueChanges
    public filterPosition: DOMRect = null
    private activeDriverSubject = new BehaviorSubject<IDriverDetailed | null>(null);
    public activeDriver$ = this.activeDriverSubject.asObservable();
    private activeDriverAttachmentsSubject = new BehaviorSubject<IDriverAttachmentInfo[]>([]);
    public activeDriverAttachments$ = this.activeDriverAttachmentsSubject.asObservable();
    private driversSubject = new BehaviorSubject<IDriver[]>([]);
    public drivers$ = this.driversSubject.asObservable();
    private readonly SESSION_STORAGE_KEY: string = 'SESSION_STORAGE_DRIVERS_KEY';
    private sessionStorageInitiated = false
    private activeRequest: boolean;

    constructor(private driverHttp: DriverHttpService, private fb: FormBuilder) {
    }

    public get filterIsActive(): boolean {
        const {filter_status, filter_error, filter_node} = this.getFiltersFormValue()
        return (filter_status !== null || filter_error !== null || filter_node !== null)
    }

    public get modalFilterControl() {
        return this.filterForm.get('modalFilter')
    }

    public get searchResult() {
        return this.activeRequest ? null :
            this.filterForm.get('filter_str').value ? this.driversSubject.value.length :
                null
    }

    public get searchActive(): boolean {
        return !!this.filterForm.get('filter_str').value
    }

    public selectSearchResult(index: number) {
        this.setActiveDriver(this.driversSubject.value[index - 1]).subscribe()
    }

    public getDriverAccounts(id: number) {
        return this.driverHttp.getDriverAccounts(id)
            .pipe(
                tap((value) => this.activeDriverAccountsSubject.next(value))
            )
    }

    public initSessionStorage() {
        if (this.sessionStorageInitiated) return
        const settings = this.getSettingsFromSessionStorage()
        if (settings) {
            this.filterForm.patchValue(settings)
        }

        this.filterChange$
            .pipe(untilDestroyed(this))
            .subscribe(v => {
                const settings = structuredClone(v)
                this.setSettingsToSessionStorage(settings)
            })

        this.sessionStorageInitiated = true
    }

    public setFilterString(str: string) {
        if (this.filterForm.get('filter_str').value === str) return
        this.filterForm.get('filter_str').patchValue(str)
    }

    public getDrivers(useFilter = false): Observable<IDriver[]> {
        this.activeRequest = true
        return this.driverHttp.getDrivers(useFilter ? this.getFiltersFormValue() : {})
            .pipe(
                tap(drivers => {
                    this.driversSubject.next(drivers);
                    this.activeRequest = false

                    if (this.searchActive && !this.activeDriverSubject.value) {
                        this.selectSearchResult(1)
                    }
                }),
            );
    }

    public getFiltersFormValue(): DriversFilter {
        return {
            ...this.modalFilterControl.value,
            filter_str: this.filterForm.get('filter_str').value,
        }
    }

    public getDriverTree(id: number): Observable<IDriverTree> {
        return this.driverHttp.getDriverTree(id)
    }

    public getDriverTreeChildren(driverId: number, driverTreeItemId: number): Observable<IDriverTree> {
        return this.driverHttp.getDriverTreeChildren(driverId, driverTreeItemId)
    }

    public updateDriverTree(id: number): Observable<string> {
        return this.driverHttp.updateDriverTree(id)
    }

    public setActiveDriver(driver: IDriver | null): Observable<IDriverDetailed | null> {
        if (driver) {
            return this.driverHttp.getDriverById(driver.id)
                .pipe(tap((driver) => this.activeDriverSubject.next(driver)))
        } else {
            return of(null)
                .pipe(tap(() => this.activeDriverSubject.next(null)))
        }

    }

    public submitDriver(driverInfo: IDriverSaveData, id: number) {
        if (id) {
            return this.driverHttp.editDriver(id, driverInfo)
                .pipe(tap(driver => {
                    this.driversSubject.next(this.driversSubject.value.map(d => {
                        if (d.id === driver.id) {
                            return driver
                        } else {
                            return d
                        }
                    }))
                }))
        } else {
            if (!driverInfo.descr) {
                driverInfo.descr = ''
            }
            return this.driverHttp.createDriver(driverInfo)
                .pipe(
                    tap(driver => {
                        this.activeDriverSubject.next(driver)
                        this.driversSubject.next([...this.driversSubject.value, driver])
                    }))
        }
    }

    public deleteDriver(id: number): Observable<string> {
        return this.driverHttp.deleteDriver(id)
            .pipe(tap(() => {
                this.activeDriverSubject.next(null)
                this.driversSubject.next(this.driversSubject.value.filter(driver => driver.id !== id))
            }))
    }

    public toggleDriver(id: number, needRun: boolean): Observable<IDriver> {
        return this.driverHttp.getDriverById(id).pipe(
            switchMap((driver) => {
                let driverSaveData: IDriverSaveData = {
                    run_setting: driver.run_setting
                };
                delete driverSaveData.run_setting.bind_to_default_node;
                delete driverSaveData.run_setting.default_node_id;
                driverSaveData.run_setting.need_run = needRun;
                return this.driverHttp.editDriver(id, driverSaveData)
                    .pipe(tap(driver => {
                        this.driversSubject.next(this.driversSubject.value.map(d => {
                            if (d.id === driver.id) {
                                return driver
                            } else {
                                return d
                            }
                        }))
                        if (this.activeDriverSubject.value && this.activeDriverSubject.value.id === driver.id) {
                            this.activeDriverSubject.next(driver)
                        }
                    }))
            })
        )
    }

    public addBlankDriver() {
        this.activeDriverSubject.next({
            id: null,
            name: null,
            node_id: null,
            descr: null,
            driver_status: null,
            driver_info: {
                type_id: null,
                type_name: null,
                has_tree: null,
                settings: []
            },
            run_setting: {
                bind_to_default_node: false,
                default_node_id: null,
                need_run: null
            },
            tm_change_run_status: 0
        })
    }

    public getDriverAttachmentsInfo(id: number) {
        this.activeDriverAttachmentsSubject.next([]);
        return this.driverHttp.getDriverAttachmentsInfo(id)
            .pipe(tap((files) => this.activeDriverAttachmentsSubject.next(files)));
    }

    public addAttachmentToDriver(id: number, file: File) {
        return from(this.getBase64(file))
            .pipe(
                map((base64: string) => {
                    return {data: base64, name: file.name, size: file.size}
                }),
                switchMap((saveData: IDriverAttachmentSaveData) => this.driverHttp.addAttachmentToDriver(id, saveData)),
                tap(((attachment: IDriverAttachmentInfo) => this.activeDriverAttachmentsSubject.next([...this.activeDriverAttachmentsSubject.value, attachment])))
            )
    }

    public downloadAttachment(id: number, name: string) {
        return this.driverHttp.getDriverAttachment(id)
            .pipe(tap((file) => {
                const attachment = new File([file], name, {type: "text/plain"});
                const a = document.createElement("a");
                const url = URL.createObjectURL(attachment);
                a.href = url;
                a.download = name;
                a.click();
            }))
    }

    public deleteAttachment(id: number) {
        return this.driverHttp.deleteAttachment(id)
            .pipe(
                tap(() => this.activeDriverAttachmentsSubject.next(this.activeDriverAttachmentsSubject.value.filter(item => item.id !== id))));
    }

    public clearAttachments() {
        this.activeDriverAttachmentsSubject.next([]);
    }

    public setFilterPosition(relativeElement: Element) {
        this.filterPosition = relativeElement.getBoundingClientRect()
    }

    private getSettingsFromSessionStorage() {
        return JSON.parse(sessionStorage.getItem(this.SESSION_STORAGE_KEY))
    }

    private setSettingsToSessionStorage(settings) {
        sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(settings))
    }

    private getBase64(file: File): Promise<string> {
        const fileReader = new FileReader()
        fileReader.readAsDataURL(file)
        return new Promise((resolve, reject) => {
            fileReader.onload = () => resolve(fileReader.result as string)
            fileReader.onerror = () => reject(fileReader.error)
        })
    }
}
